import { useRef, useState, type FormEvent } from 'react';
import { addDoc, collection, deleteDoc, deleteField, doc, updateDoc } from 'firebase/firestore';
import { GitCompareArrows, Link2, Pencil, Trash2, Upload, X } from 'lucide-react';
import { db } from '../../lib/firebase';
import { compressImage } from '../../lib/images';
import { useAdminCollection } from './hooks';
import { EmptyState, Field, Modal } from './adminUi';
import { GALLERY_CATEGORIES, type GalleryItem } from '../../lib/types';

export default function GalleryAdmin() {
  const { items, loading } = useAdminCollection<GalleryItem>('gallery');
  const fileInput = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState('');
  const [uploadError, setUploadError] = useState('');
  const [uploadCategory, setUploadCategory] = useState<string>('exterior');
  const [urlDraft, setUrlDraft] = useState('');
  const [editing, setEditing] = useState<GalleryItem | null>(null);
  const beforeInput = useRef<HTMLInputElement>(null);
  const beforeTarget = useRef<string | null>(null);

  const onBeforeFile = async (files: FileList | null) => {
    const file = files?.[0];
    const id = beforeTarget.current;
    if (!file || !id) return;
    setUploadError('');
    try {
      const dataUrl = await compressImage(file);
      await updateDoc(doc(db(), 'gallery', id), { beforeUrl: dataUrl });
    } catch (e: any) {
      setUploadError(e?.message ?? 'Before-photo upload failed');
    } finally {
      beforeTarget.current = null;
      if (beforeInput.current) beforeInput.current.value = '';
    }
  };

  const sorted = [...items].sort((a, b) => (b.createdAt ?? 0) - (a.createdAt ?? 0));

  const onFiles = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    setUploadError('');
    try {
      const list = Array.from(files);
      for (let i = 0; i < list.length; i++) {
        setProgress(list.length > 1 ? `Photo ${i + 1} of ${list.length}…` : 'Compressing…');
        const dataUrl = await compressImage(list[i]);
        await addDoc(collection(db(), 'gallery'), {
          createdAt: Date.now(),
          url: dataUrl,
          storagePath: '',
          caption: list[i].name.replace(/\.[^.]+$/, '').replace(/[-_]/g, ' '),
          category: uploadCategory,
        });
      }
    } catch (e: any) {
      setUploadError(e?.message ?? 'Upload failed');
    } finally {
      setUploading(false);
      setProgress('');
      if (fileInput.current) fileInput.current.value = '';
    }
  };

  const addByUrl = async (e: FormEvent) => {
    e.preventDefault();
    if (!urlDraft.trim()) return;
    setUploadError('');
    try {
      await addDoc(collection(db(), 'gallery'), {
        createdAt: Date.now(),
        url: urlDraft.trim(),
        storagePath: '',
        caption: 'New photo',
        category: uploadCategory,
      });
      setUrlDraft('');
    } catch (e: any) {
      setUploadError(e?.message ?? 'Failed to add');
    }
  };

  const remove = async (g: GalleryItem) => {
    if (!confirm('Delete this photo?')) return;
    await deleteDoc(doc(db(), 'gallery', g.id));
  };

  const saveEdit = async () => {
    if (!editing) return;
    await updateDoc(doc(db(), 'gallery', editing.id), {
      caption: editing.caption,
      category: editing.category,
    });
    setEditing(null);
  };

  return (
    <div className="mx-auto max-w-5xl">
      <h1 className="font-display text-2xl font-bold text-white">Gallery</h1>
      <p className="mt-1 text-sm text-zinc-500">
        Upload photos of your work — they're compressed automatically and appear on the site
        immediately. Starter photos are stock; replace them with real results as you go.
      </p>

      <div className="card mt-6 p-5">
        <div className="flex flex-wrap items-end gap-4">
          <div>
            <label className="label">Category for new photos</label>
            <select className="input !w-auto capitalize" value={uploadCategory} onChange={(e) => setUploadCategory(e.target.value)}>
              {GALLERY_CATEGORIES.map((c) => (
                <option key={c} value={c} className="capitalize">{c}</option>
              ))}
            </select>
          </div>
          <div className="flex-1" />
          <input ref={fileInput} type="file" accept="image/*" multiple hidden onChange={(e) => onFiles(e.target.files)} />
          <input ref={beforeInput} type="file" accept="image/*" hidden onChange={(e) => onBeforeFile(e.target.files)} />
          <button className="btn-primary !py-2.5 text-sm" onClick={() => fileInput.current?.click()} disabled={uploading}>
            <Upload size={16} /> {uploading ? progress || 'Uploading…' : 'Upload Photos'}
          </button>
        </div>
        <form onSubmit={addByUrl} className="mt-4 flex gap-2.5 border-t border-white/[0.06] pt-4">
          <input
            className="input flex-1"
            value={urlDraft}
            onChange={(e) => setUrlDraft(e.target.value)}
            placeholder="…or paste an image URL and press Add"
          />
          <button className="btn-ghost !px-4 !py-2.5 text-sm" disabled={!urlDraft.trim() || uploading}>
            <Link2 size={15} /> Add
          </button>
        </form>
        {uploadError && <p className="mt-3 text-sm text-red-400">{uploadError}</p>}
      </div>

      {loading ? (
        <EmptyState text="Loading…" />
      ) : sorted.length === 0 ? (
        <EmptyState text="No photos in the database yet — upload some, or publish starter content from the Dashboard." />
      ) : (
        <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
          {sorted.map((g) => (
            <div key={g.id} className="group relative overflow-hidden rounded-2xl border border-white/10">
              <img src={g.url} alt={g.caption} className="aspect-square w-full object-cover" loading="lazy" />
              {g.beforeUrl && (
                <span className="absolute left-2 top-2 rounded-full bg-accent-500/90 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-white">
                  Before / After
                </span>
              )}
              <div className="absolute inset-0 flex flex-col justify-between bg-gradient-to-t from-night-950/90 via-night-950/20 to-transparent p-3 opacity-0 transition-opacity group-hover:opacity-100">
                <div className="flex justify-end gap-1.5">
                  {g.beforeUrl ? (
                    <button
                      className="rounded-lg bg-white/15 p-2 text-white backdrop-blur hover:bg-amber-500/80"
                      title="Remove before photo"
                      onClick={() => updateDoc(doc(db(), 'gallery', g.id), { beforeUrl: deleteField() })}
                    >
                      <X size={14} />
                    </button>
                  ) : (
                    <button
                      className="rounded-lg bg-white/15 p-2 text-white backdrop-blur hover:bg-white/25"
                      title='Add a "before" photo to make this a before/after slider'
                      onClick={() => {
                        beforeTarget.current = g.id;
                        beforeInput.current?.click();
                      }}
                    >
                      <GitCompareArrows size={14} />
                    </button>
                  )}
                  <button className="rounded-lg bg-white/15 p-2 text-white backdrop-blur hover:bg-white/25" onClick={() => setEditing(g)}>
                    <Pencil size={14} />
                  </button>
                  <button className="rounded-lg bg-white/15 p-2 text-white backdrop-blur hover:bg-red-500/80" onClick={() => remove(g)}>
                    <Trash2 size={14} />
                  </button>
                </div>
                <div>
                  <p className="line-clamp-2 text-xs font-medium text-white">{g.caption}</p>
                  <p className="text-[11px] capitalize text-accent-300">{g.category}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <Modal title="Edit Photo" onClose={() => setEditing(null)}>
          <img src={editing.url} alt="" className="max-h-56 w-full rounded-xl object-cover" />
          <div className="mt-4 grid gap-4">
            <Field label="Caption">
              <input className="input" value={editing.caption} onChange={(e) => setEditing({ ...editing, caption: e.target.value })} />
            </Field>
            <Field label="Category">
              <select className="input capitalize" value={editing.category} onChange={(e) => setEditing({ ...editing, category: e.target.value })}>
                {GALLERY_CATEGORIES.map((c) => (
                  <option key={c} value={c} className="capitalize">{c}</option>
                ))}
              </select>
            </Field>
          </div>
          <div className="mt-6 flex justify-end gap-3">
            <button className="btn-ghost !py-2.5 text-sm" onClick={() => setEditing(null)}>Cancel</button>
            <button className="btn-primary !py-2.5 text-sm" onClick={saveEdit}>Save</button>
          </div>
        </Modal>
      )}
    </div>
  );
}
